import { useQuery } from "@apollo/client/react";
import _ from "lodash";
import { useMemo } from "react";

import type { EntityFragment } from "@/__generated__/graphql";
import { useOpenRecord } from "@/app/app/_/routing";
import { SelectableCardList } from "@/components/molecules/SelectableCardList";
import {
  SELECTABLE_LIST_ITEM_ROW_WITH_OVERLINE_HEIGHT,
  SelectableListItemRow,
  SelectableListItemRowOverline,
  SelectableListItemRowTitleEnd,
} from "@/components/molecules/SelectableListItemRow";
import { Queries } from "@/constants";
import { Utils } from "@/utils";

type EntityDocumentsPanelProps = {
  entity: EntityFragment;
};

type CompanyDocumentRow = {
  id: string;
  identifier: string;
  name?: string | null;
  description?: string | null;
  type?: string | null;
  dateIssued?: string | null;
  createdAt?: string | null;
};

/** What the list's rows are while it loads: a typical one of these records. */
const COMPANY_DOCUMENT_PLACEHOLDER: CompanyDocumentRow = {
  id: "placeholder",
  identifier: "placeholder",
  name: "Articles of incorporation",
  description: "Filed with the registrar on incorporation",
  type: "INCORPORATION_DOCUMENTS",
  dateIssued: "2024-03-14T00:00:00Z",
  createdAt: "2024-03-14T00:00:00Z",
};

/**
 * The business's own records - its incorporation papers, minutes, share
 * certificates, agreements and filings - newest first. Loaded here rather than
 * with the entity, as a group's rows are; each opens as its own page.
 */
export function EntityDocumentsPanel({ entity }: EntityDocumentsPanelProps) {
  const openRecord = useOpenRecord();
  const { data, loading } = useQuery(Queries.ENTITY_COMPANY_DOCUMENTS_QUERY, {
    variables: { id: entity.id },
    fetchPolicy: "cache-and-network",
  });
  const documents = useMemo<CompanyDocumentRow[]>(
    () =>
      data?.node?.__typename === "EntityObject"
        ? data.node.companyDocuments
        : [],
    [data],
  );

  return (
    <div
      // A list, on the recessed surface lists sit on.
      className="flex-1 min-h-0 w-full flex flex-col overflow-hidden bg-muted dark:bg-[#151719]"
      data-testid="entity-company-documents"
    >
      <SelectableCardList
        size="sm"
        variant="outlined"
        layout="cards"
        estimatedItemHeight={SELECTABLE_LIST_ITEM_ROW_WITH_OVERLINE_HEIGHT}
        items={documents}
        loading={loading && documents.length === 0}
        placeholderItem={COMPANY_DOCUMENT_PLACEHOLDER}
        getItemKey={(document) => document.id}
        onItemClick={(document) =>
          openRecord({ id: document.id, identifier: document.identifier })
        }
        emptyState={{
          title: "No documents yet",
          description:
            "Incorporation papers, bylaws, minutes, share certificates and agreements dropped in a thread show here.",
        }}
        renderItem={({ item: document, isLoading }) => {
          const issued = document.dateIssued ?? document.createdAt;
          return (
            <SelectableListItemRow
              overline={
                document.type ? (
                  <SelectableListItemRowOverline>
                    {_.upperFirst(_.lowerCase(document.type))}
                  </SelectableListItemRowOverline>
                ) : null
              }
              overlineEnd={
                issued ? (
                  <SelectableListItemRowTitleEnd>
                    {Utils.fDate(new Date(issued))}
                  </SelectableListItemRowTitleEnd>
                ) : null
              }
              title={document.name || "Document"}
              description={document.description || null}
              isLoading={isLoading}
              rootDataAttrs={{
                "data-testid": "entity-company-document",
                "data-document-id": document.identifier,
              }}
            />
          );
        }}
      />
    </div>
  );
}
